import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { type StockArticle } from "@/lib/stock.api";
import { useRestockArticleMutation } from "@/hooks/mutations/useRestockArticle";
import { useRequestStockEditMutation } from "@/hooks/mutations/useRequestStockEdit";
import { ResponsibleAgentSelect } from "@/components/ResponsibleAgentSelect";
import { useAuth } from "@/App";

interface RestockDialogProps {
  article: StockArticle | null;
  // true for admin/super_admin — ADJUST is applied directly. false for manager —
  // ADJUST goes through the modification-request workflow. IN is always direct.
  canAdjustDirect: boolean;
  onClose: () => void;
  onDone: () => void;
}

export function RestockDialog({ article, canAdjustDirect, onClose, onDone }: RestockDialogProps) {
  const { user } = useAuth();
  const canPickResponsible = user ? ["manager", "admin", "super_admin"].includes(user.role) : false;
  const [type, setType] = useState<"IN" | "ADJUST">("IN");
  const [quantity, setQuantity] = useState(0);
  const [reason, setReason] = useState("");
  const [responsibleAgentId, setResponsibleAgentId] = useState<number | undefined>(undefined);
  const restockMutation = useRestockArticleMutation();
  const requestMutation = useRequestStockEditMutation();

  useEffect(() => {
    if (article) {
      setType("IN");
      setQuantity(0);
      setReason("");
      setResponsibleAgentId(undefined);
    }
  }, [article]);

  const needsRequest = type === "ADJUST" && !canAdjustDirect;

  function handleSubmit() {
    if (!article) return;

    if (needsRequest) {
      requestMutation.mutate(
        {
          articleId: article.id,
          targetType: "stock_adjustment",
          reason,
          proposedChanges: { quantity },
        },
        {
          onSuccess: () => {
            setQuantity(0);
            setReason("");
            onDone();
            onClose();
          },
        },
      );
      return;
    }

    restockMutation.mutate(
      {
        id: article.id,
        type,
        quantity: type === "IN" ? Math.abs(quantity) : quantity,
        responsibleAgentId,
      },
      {
        onSuccess: () => {
          setQuantity(0);
          onDone();
          onClose();
        },
      },
    );
  }

  const pending = restockMutation.isPending || requestMutation.isPending;
  const submitDisabled = pending || quantity === 0 || (needsRequest && !reason.trim());

  return (
    <Dialog
      open={Boolean(article)}
      onOpenChange={(v) => {
        if (!v) onClose();
      }}
    >
      <DialogContent>
        {article && (
          <>
            <DialogHeader>
              <DialogTitle>Réapprovisionner — {article.name}</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <p className="text-[11.5px] text-muted-foreground">
                Stock actuel : {article.onHand} {article.unit}
              </p>
              <div className="grid grid-cols-2 rounded-lg border border-border overflow-hidden">
                <button
                  type="button"
                  onClick={() => setType("IN")}
                  className={`px-3 py-2 text-[12px] font-medium ${type === "IN" ? "bg-brand-gold-dark text-white" : "bg-card text-muted-foreground"}`}
                >
                  Entrée (réappro)
                </button>
                <button
                  type="button"
                  onClick={() => setType("ADJUST")}
                  className={`px-3 py-2 text-[12px] font-medium ${type === "ADJUST" ? "bg-brand-gold-dark text-white" : "bg-card text-muted-foreground"}`}
                >
                  Ajustement
                </button>
              </div>
              <div>
                <label className="block text-[11.5px] font-medium text-body mb-1.5">
                  {type === "IN" ? "Quantité reçue" : "Ajustement (+/-)"}
                </label>
                <Input
                  type="number"
                  value={quantity}
                  onChange={(e) => setQuantity(parseInt(e.target.value) || 0)}
                />
                {type === "ADJUST" && (
                  <p className="text-[10.5px] text-muted-foreground mt-1">
                    Négatif pour retirer, positif pour ajouter.
                  </p>
                )}
              </div>

              {!needsRequest && canPickResponsible && (
                <ResponsibleAgentSelect value={responsibleAgentId} onChange={setResponsibleAgentId} />
              )}

              {needsRequest && (
                <>
                  <p className="text-[11px] text-muted-foreground">
                    Un ajustement doit être validé — le stock ne changera qu'après approbation par
                    un administrateur.
                  </p>
                  <div>
                    <label className="block text-[11.5px] font-medium text-body mb-1.5">
                      Raison de l'ajustement (obligatoire)
                    </label>
                    <textarea
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      rows={2}
                      placeholder="Pourquoi cet ajustement est-il nécessaire ?"
                      className="flex w-full rounded border border-border bg-card px-3 py-2 text-sm resize-none"
                    />
                  </div>
                </>
              )}
            </div>
            <DialogFooter>
              <Button variant="secondary" onClick={onClose}>
                Annuler
              </Button>
              <Button onClick={handleSubmit} disabled={submitDisabled}>
                {pending ? (
                  <Loader2 size={13} className="animate-spin" />
                ) : needsRequest ? (
                  "Soumettre la demande"
                ) : (
                  "Confirmer"
                )}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
